<template>
  <div class="vap-page">
    <Loading
      class="vasion-loading-indicator"
      :active.sync="isLoading"
      :is-full-page="false"
      :color="loaderColor"
      loader="dots"
      :background-color="loaderBackgroundColor"
    />
    <div
      v-show="!creatingNewForm"
      v-if="activeForm"
      id="form-name"
      class="vap-page-header"
    >
      <h1>
        <VasionInput
          id="new-form-title"
          v-model="formName"
          name="new-form-title"
          class="input-style"
          inputType="title-input"
          :isTitleInput="true"
          @input="markAsDirty"
        />
      </h1>
      <div class="btns-container">
        <VasionButton
          v-show="!creatingNewForm"
          id="index-form-submit"
          class="last-btn"
          :classProp="'primary'"
          :isDisabled="!areFormsDirty || editingField"
          :ripple="true"
          @vasionButtonClicked="submitForm()"
        >
          Save {{ $formsLabel }}
        </VasionButton>
      </div>
    </div>
    <div v-if="activeForm" class="vap-page-content">
      <form v-show="creatingNewForm" id="create-new-form" @keydown.enter.prevent="addNewForm">
        <VasionInput
          id="new-form-name"
          v-model="newFormName"
          name="new-form-name"
          class="input-style"
          inputType="top-white"
          :autoFocus="true"
          :title="`NEW ${$formsLabel.toUpperCase()} NAME`"
          :placeholder="`Enter ${$formsLabel} Name...`"
          :trimValue="true"
          @input="markAsDirty"
        />
        <VasionButton id="index-new-form-cancel" :classProp="'text'" @vasionButtonClicked="goToAllForms()">
          CANCEL
        </VasionButton>
        <VasionButton
          id="index-new-form-submit"
          :classProp="'primary'"
          :isDisabled="!newFormName"
          @vasionButtonClicked="addNewForm()"
        >
          CREATE
        </VasionButton>
      </form>
      <div v-show="!creatingNewForm" class="md-layout tab-div">
        <VasionButton id="tab-field-table" :classProp="calculatedClass('fields')" @vasionButtonClicked="updateSelectedTab('fields')">
          {{ $fieldsLabelPlural }}
        </VasionButton>
        <VasionButton id="tab-form-layouts" :classProp="calculatedClass('layouts')" @vasionButtonClicked="updateSelectedTab('layouts')">
          {{ $layoutsLabelPlural }}
        </VasionButton>
        <VasionButton id="tab-security" :classProp="calculatedClass('security')" @vasionButtonClicked="updateSelectedTab('security')">
          Permissions
        </VasionButton>
        <VasionButton
          id="tab-packages"
          :classProp="calculatedClass('packages')"
          @vasionButtonClicked="updateSelectedTab('packages')"
        >
          Packages
        </VasionButton>
        <VasionButton
          id="tab-api"
          :classProp="calculatedClass('api')"
          @vasionButtonClicked="updateSelectedTab('api')"
        >
          Integrations
        </VasionButton>
      </div>
      <div v-show="!creatingNewForm && selectedTabName === 'fields'" id="fields-tab" class="">
        <div class="md-layout">
          <div class="row">
            <VasionInput
              v-model="template"
              class="input-style row-field"
              inputType="top-white"
              title="FILE NAME FORMAT"
              :width="'424'"
              placeholder="Enter File Name Format..."
              @input="markAsDirty"
            />
            <VasionCheckbox
              id="rename-files"
              name="rename-files"
              class="row-field checkbox"
              :checked="renameFiles"
              @change="toggleCheckbox('renameFiles')"
            >
              Rename Files
            </VasionCheckbox>
          </div>
          <div class="row">
            <VasionDropList
              v-slot="slotItem"
              v-model="selectedField"
              class="fullWidth"
              :dataArray="fields"
              width="424"
              placeholder="..."
              type="plain-list"
              displayName="title"
              valueName="title"
            >
              {{ slotItem.item.title }}
            </VasionDropList>
            <VasionButton
              :classProp="'secondary'"
              :isRaised="false"
              :isDisabled="!selectedField.title"
              :ripple="true"
              @vasionButtonClicked="addFieldToFormat()"
            >
              Add To Format
            </VasionButton>
          </div>
        </div>
        <table class="field-header-table">
          <tr>
            <td>
              <label class="vasion-page-subheader">{{ $fieldsLabelPlural }}</label>
            </td>
            <td class="field-header-filter-column">
              <VasionInput
                id="field-filter-input"
                v-model="fieldFilterText"
                class="search-padding"
                name="field-filter-input"
                placeholder="Quick find..."
                inputType="search-gray"
                @input="markAsDirty"
              />
            </td>
            <td class="field-header-add-field-column">
              <VasionButton
                class="last-btn"
                :classProp="'secondary'"
                :isRaised="false"
                :isDisabled="false"
                :ripple="true"
                @vasionButtonClicked="$refs.newFieldDialog.toggleAddNewField()"
              >
                Add Field
              </VasionButton>
            </td>
          </tr>
        </table>
        <div id="field-list" class="fields-table-container vasion-html-table-default">
          <table>
            <thead>
              <tr>
                <th class="move-icon-column" />
                <th class="fields-column">
                  <label class="vasion-html-table-header">Display Label</label>
                </th>
                <th class="fields-column">
                  <label class="vasion-html-table-header">Unique Name</label>
                </th>
                <th class="field-mandatory-column fields-column">
                  <label class="vasion-html-table-header">Mandatory</label>
                </th>
                <th class="field-type-column fields-column">
                  <label class="vasion-html-table-header">Field Type</label>
                </th>
                <th class="controls-column" />
              </tr>
            </thead>
            <tbody class="scrollTBody">
              <tr
                v-for="(field, index) in filteredFields()"
                :key="index"
                :class="{'lightestGreyBackground': index % 2 === 0, 'dropTarget': index == dragRowTargetIndex }" 
                :draggable="!fieldFilterText"
                @dragstart="dragRowStart($event, index)"
                @dragover="dragRowOver($event, index)"
                @drop="dragRowDrop($event, index)"
                @dragend="dragRowEnd()"
              >
                <td class="move-icon-column" :class="{ 'move-icon' : !fieldFilterText }">
                  <VasionMoveIcon v-if="!fieldFilterText" />
                </td>
                <td
                  class="pointer fields-column" 
                  :draggable="!fieldFilterText"
                  @dragstart="dragRowChildPrevent($event, index)"
                  @click="toggleFieldEdit(field.name)"
                >
                  <label class="vasion-html-table-field pointer">{{ field.title }}</label>
                </td>
                <td
                  class="pointer fields-column"
                  :draggable="!fieldFilterText"
                  @dragstart="dragRowChildPrevent($event, index)"
                  @click="toggleFieldEdit(field.name)"
                >
                  <label class="vasion-html-table-field pointer">{{ field.name }}</label>
                </td>
                <td
                  class="field-mandatory-column pointer fields-column"
                  :draggable="!fieldFilterText"
                  @dragstart="dragRowChildPrevent($event, index)"
                  @click="toggleFieldEdit(field.name)"
                >
                  <label class="vasion-html-table-field pointer">{{ field.mandatory ? 'Yes' : 'No' }}</label>
                </td>
                <td
                  class="field-type-column pointer fields-column"
                  :draggable="!fieldFilterText"
                  @dragstart="dragRowChildPrevent($event, index)"
                  @click="toggleFieldEdit(field.name)"
                >
                  <label class="vasion-html-table-field pointer">{{ fieldTypeText(field.type) }}</label>
                </td>
                <td
                  class="controls-column right-text-align"
                  :draggable="!fieldFilterText"
                  @dragstart="dragRowChildPrevent($event, index)"
                >
                  <VasionButton
                    v-show="!fieldFilterText"
                    :isRaised="false"
                    :isDense="false"
                    :icon="'VasionArrowLargeUpward'"
                    :isDisabled="!shouldReorderUpShow(index)"
                    title="Move Field Up"
                    @vasionButtonClicked="reorderField('up', field.name)"
                  />
                  <VasionButton
                    v-show="!fieldFilterText"
                    :isRaised="false"
                    :isDense="false"
                    :icon="'VasionArrowLargeDownward'"
                    :isDisabled="!shouldReorderDownShow(index)"
                    title="Move Field Down"
                    @vasionButtonClicked="reorderField('down', field.name)"
                  />
                  <VasionButton
                    :isRaised="false"
                    :isDense="false"
                    :icon="'VasionEllipsisIcon'"
                    @vasionButtonClicked="ellipsisOpen($event, field, 'field')"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div v-show="!creatingNewForm && selectedTabName === 'layouts'" id="layouts-tab">
        <table class="field-header-table">
          <tr>
            <td>
              <label class="vasion-page-subheader">All {{ $layoutsLabelPlural }}</label>
            </td>
            <td class="field-header-filter-column">
              <VasionInput
                id="layout-filter-input"
                v-model="layoutFilterText"
                class="search-padding"
                name="layout-filter-input"
                placeholder="Quick find..."
                inputType="search-gray"
              />
            </td>
            <td class="field-header-add-field-column">
              <VasionButton
                id="new-form-button"
                class="last-btn"
                :classProp="'secondary'"
                :isRaised="false"
                :isDisabled="!containsMoreThanWorkflowFields"
                :ripple="true"
                @vasionButtonClicked="newLayoutClick"
              >
                New {{ $layoutsLabel }}
              </VasionButton>
            </td>
          </tr>
        </table>
        <div id="field-list" class="forms-table-container vasion-html-table-default">
          <table>
            <thead>
              <tr>
                <th class="icon-table-column" />
                <th class="layout-name-table-column">
                  <label class="vasion-html-table-header">{{ $layoutsLabel }} Name</label>
                </th>
                <th>
                  <label class="vasion-html-table-header">Description</label>
                </th>
                <th class="icon-table-column" />
              </tr>
            </thead>
            <tbody class="scrollTBodyFields">
              <tr v-for="(formLayout, index) in filteredLayoutArray" :key="index" :class="{'lightestGreyBackground': index % 2 === 0 }">
                <td class="icon-table-column pointer" @click="editFormLayout(formLayout.EFormID)">
                  <VasionLayoutIcon class="pointer" />
                </td>
                <td class="layout-name-table-column pointer" @click="editFormLayout(formLayout.EFormID)">
                  <label class="vasion-html-table-field pointer">{{ formLayout.Name }}</label>
                </td>
                <td class="pointer" @click="editFormLayout(formLayout.EFormID)">
                  <label class="vasion-html-table-field pointer">{{ formLayout.Description }}</label>
                </td>
                <td class="controls-column right-text-align">
                  <VasionButton
                    :id="`form-layout-ellipsis-${formLayout.EFormID}`"
                    :isRaised="false"
                    :isDense="false"
                    :icon="'VasionEllipsisIcon'"
                    @vasionButtonClicked="ellipsisOpen($event, formLayout, 'form-layout')"
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="!formLayoutArray || formLayoutArray.length === 0" class="no-form-layouts-div">
            <div>
              <VasionLargeLayoutIcon />
            </div>
            <div class="no-form-layouts-label-div">
              <label class="vasion-page-subheader">No {{ $layoutsLabelPlural }} Added to {{ $formsLabel }}</label>
            </div>
            <div class="no-form-layouts-label-div">
              <label class="vasion-page-subheader-light">{{ $layoutsLabelPlural }} created for this {{ $formsLabel }} will appear here</label>
            </div>
          </div>
        </div>
      </div>
      <div v-show="!creatingNewForm && selectedTabName === 'security'" id="security-tab">
        <UserGroupPermissions
          :users="users"
          :groups="groups"
          :initial-permissions="currentPermissions"
          @permissionsChanged="onPermissionsChanged"
          ref="permissionsComponent"
        />
      </div>
      <div v-show="!creatingNewForm && selectedTabName === 'packages'" id="packages-tab" class="">
        <table class="field-header-table">
          <tr>
            <td>
              <label class="vasion-page-subheader mb-0">All Packages</label>
            </td>
            <td class="field-header-add-package-column">
              <VasionButton
                class="last-btn"
                :classProp="'secondary'"
                :isRaised="false"
                :ripple="true"
                @vasionButtonClicked="newPackageClick"
              >
                New Package
              </VasionButton>
            </td>
          </tr>
        </table>
        <div id="field-list" class="packages-table-container vasion-html-table-default">
          <table class="vasion-table-packages">
            <thead>
              <tr>
                <th></th>
                <th>Package Name</th>
                <th>Package Field</th>
                <th>Active</th>
                <th></th>
              </tr>
            </thead>
            <tbody class="scrollTBodyFields">
              <tr v-for="(pkg, index) in packages" :key="index" :class="{'lightestGreyBackground': index % 2 === 0 }">
                <td @click="editPackage(pkg.PackageID)"><VasionFolderAdd/></td>
                <td @click="editPackage(pkg.PackageID)">{{ pkg.Name }}</td>
                <td @click="editPackage(pkg.PackageID)">{{ pkg.PackageField }}</td>
                <td @click="editPackage(pkg.PackageID)">{{ pkg.active }}</td>
                <td :class="{'fix-last-col-width': packages.length < 16 }">
                  <VasionButton
                    :isRaised="false"
                    :isDense="false"
                    :icon="'VasionEllipsisIcon'"
                    @vasionButtonClicked="ellipsisOpen($event, pkg, pkg.IsDisabled ? 'enable-package' : 'disable-package')"
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="!packages || packages.length === 0" class="no-form-layouts-div">
            <div>
              <VasionLargeLayoutIcon />
            </div>
            <div class="no-form-layouts-label-div">
              <label class="vasion-page-subheader">No Packages Added to Object</label>
            </div>
            <div class="no-form-layouts-label-div">
              <label class="vasion-page-subheader-light">Packages created for this Object will appear here</label>
            </div>
          </div>
        </div>
      </div>
      <div v-show="!creatingNewForm && selectedTabName === 'api'" id="api-tab">
        <ApiLookupsList />
      </div>
      <NewFieldDialog
        ref="newFieldDialog"
        :config="newFieldConfig"
        :fieldTypeList="fieldTypeList"
        :fields.sync="fields"
        @update:fields="updateTable"
        @promptUpdateAllFields="promptUpdateField"
      />
    </div>

    <VasionGeneralModal
      id="confirmLeave"
      :hideButtons="true"
      modalType="slot"
      :sync="showUpdateFieldsPrompt"
      @noButtonClick="showUpdateFieldsPrompt = !showUpdateFieldsPrompt"
    >
      <VasionConfirmationDialog :message="updatePromptMessage" @noButtonClick="showUpdateFieldsPrompt = !showUpdateFieldsPrompt" @yesButtonClick="updateFieldsInForms()" />
    </VasionGeneralModal>

    <md-dialog id="confirmLeave" :md-active.sync="showLeaveDialog">
      <VasionConfirmationDialog :message="leaveMessage" @noButtonClick="cancelLeave()" @yesButtonClick="doLeave()" />
    </md-dialog>

    <md-dialog id="confirmLeave" :md-active.sync="showDisablePackagerPreventionPrompt">
      <VasionConfirmationDialog
        message="This package will be disabled. You will no longer be able to submit this package. Do you want to proceed with this action?"
        @noButtonClick="showDisablePackagerPreventionPrompt = false; currentSelectedPackage = {}"
        @yesButtonClick="changePackageStatus(currentSelectedPackage, false)" />
    </md-dialog>

    <md-dialog id="confirmLeave" :md-active.sync="showMustDisablePackageToDeletePrompt">
      <VasionConfirmationDialog
        message="Package is currently in use. The package must be disabled before deleting."
        yesButtonText = "OK"
        :showNoButton = false
        @yesButtonClick="showMustDisablePackageToDeletePrompt = false; currentSelectedPackage = {}" />
    </md-dialog>

    <md-dialog id="confirmLeave" :md-active.sync="showDeleteConfirmation">
      <VasionConfirmationDialog
        message="Are you sure you want to delete this package?  All documents associated with this package will no longer be part of this package. The documents will not be deleted."
        @noButtonClick="showDeleteConfirmation = false; currentSelectedPackage = {}"
        @yesButtonClick="deletePackage(currentSelectedPackage); showDeleteConfirmation = false" />
    </md-dialog>

    <VasionEllipsisMenu
      v-if="showEllipsisMenu"
      v-click-outside="ellipsisClose"
      :buttonConfig="ellipsisButtonConfig"
      :style="ellipsisMenuTopStyle"
      :rowItem="ellipsisRowItem"
      @ellipsis-button-clicked="ellipsisButtonClicked"
      @close="ellipsisClose"
    />

    <VasionGeneralModal
      id="vasion-confirmation-div"
      :confirmButtonText="'YES'"
      :rejectButtonText="'NO'"
      :modalType="'message'"
      :buttonGroup="'center'"
      :message="generalModalMessage"
      :sync="showGeneralModal"
      @confirmButtonClick="generalModalOKClick()"
      @noButtonClick="showGeneralModal = false"
    />

    <VasionSnackbar
      id="results-snack"
      :showSnackbarBool.sync="showSnackbar"
      :snackbarImage="snackbarImage"
      :snackbarSubTitle="snackbarSubTitle"
      :snackbarTitle="snackbarTitle"
    />
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay';
// Import stylesheet
import 'vue-loading-overlay/dist/vue-loading.css';
import NewFieldDialog from '@/components/forms/NewFieldDialog.vue';
import { loaderBackgroundColor, loaderColor } from '@/assets/js/styleConfig'
import cloneDeep from 'lodash/cloneDeep'
import ApiLookupsList from '@/components/forms/api/ApiLookupsList'
import UserGroupPermissions from '@/components/shared/UserGroupPermissions.vue'

export default {
    name: 'EditAttributeForm',
    components: {
      ApiLookupsList,
      Loading,
      NewFieldDialog,
      UserGroupPermissions,
    },
    beforeRouteLeave(to, from, next) {
      if (this.$store.state.common.bypassNavigationGuards) return next()
    
      if (this.areFormsDirty && !this.routeTo && !this.saving && to.name !== 'TheLogin') {
        this.routeTo = to
        this.toggleLeaveDialog()
      } else {
        this.routeTo = null
        next()
      }
    },
    data: function () {
      return {
        accessGroups: [],
        accessUsers: [],
        attributeFormDirtyState: false,
        creatingNewForm: false,
        currentSelectedPackage: {},
        deleteFieldName: '',
        deleteFormLayoutID: 0,
        deletingEntityId: '',
        dragRowStartIndex: -1,
        dragRowTargetIndex: -1,
        editingField: false,
        editingFieldName: '',
        ellipsisButtonFieldConfig: {
          buttons: [
            {
              id: 'edit-field-button',
              name: 'Edit Field',
              icon: 'VasionEditIcon',
            },
            {
              id: 'delete-field-button',
              name: 'Delete Field',
              icon: 'VasionDeleteIcon',
            },
          ],
        },
        ellipsisButtonEnablePackageConfig: {
          buttons: [
            {
              id: 'edit-package-button',
              name: 'Edit Package',
              icon: 'VasionEditIcon',
            },
            {
              id: 'enable-package-button',
              name: 'Enable Package',
              icon: 'VasionCheckBoxCheckedIcon',
            },
            {
              id: 'delete-package-button',
              name: 'Delete Package',
              icon: 'VasionDeleteIcon',
            },
          ],
        },
        ellipsisButtonDisablePackageConfig: {
          buttons: [
            {
              id: 'edit-package-button',
              name: 'Edit Package',
              icon: 'VasionEditIcon',
            },
            {
              id: 'disable-package-button',
              name: 'Disable Package',
              icon: 'VasionCheckBoxEmptyIcon',
            },
            {
              id: 'delete-package-button',
              name: 'Delete Package',
              icon: 'VasionDeleteIcon',
            },
          ],
        },
        ellipsisButtonFormLayoutConfig: { buttons: [] },
        ellipsisOpenType: '',
        ellipsisRowItem: {},
        fieldFilterText: '',
        fieldId: 0,
        fieldOrder: 0,
        fields: [],
        fieldTypeList: [
          { id: 1, value: 'Textbox' },
          { id: 2, value: 'Dropdown' },
          { id: 3, value: 'Date' },
          { id: 6, value: 'Number' },
          { id: 7, value: 'URL' },
          { id: 8, value: 'Email' },
          { id: 9, value: 'Checkbox' },
          { id: 10, value: 'Radio Button' },
          { id: 11, value: 'Currency' },
          { id: 12, value: 'Signature Field' },
          { id: 13, value: 'Text Area' },
          { id: 14, value: 'Address' },
        ],
        formLayoutArray: [],
        formName: '',
        generalModalMessage: '',
        isLoading: false,
        layoutFilterText: '',
        loaderBackgroundColor,
        loaderColor,
        newFieldConfig: {
          allowMultiples: true,
          decimalPlaces: 0,
          fieldHidden: false,
          fieldLabel: '',
          fieldLength: 100,
          fieldType: {},
          fieldUniqueName: { name: '<New Field>' },
          fieldValues: '',
          mandatory: false,
          readOnly: false,
          showCommas: false,
          systemDate: false,
          totalPageNo: false,
          filterValues: false,
          filterFieldsList: [],
        },
        newFormName: '',
        packages: [],
        piiData: false,
        renameFiles: false,
        saving: false,
        selectedField: {},
        selectedTabName: 'fields',
        showDisablePackagerPreventionPrompt: false,
        showMustDisablePackageToDeletePrompt: false,
        showDeleteConfirmation: false,
        showEllipsisMenu: false,
        showGeneralModal: false,
        showLeaveDialog: false,
        showSnackbar: false,
        showUpdateFieldsPrompt: false,
        snackbarImage: false,
        snackbarSubTitle: '',
        snackbarTitle: '',
        template: '',
        updateFieldPayload: {},
        objectFields: [],
      }
    },
    computed: {
      activeForm() { return this.$store.state.attributeForm.activeIndexForm },
      areFormsDirty() { return this.$store.state.forms.areFormsDirty },
      containsMoreThanWorkflowFields() {
        const fieldsLocal = this.filteredFields().filter((f) => {
          if (f.name === 'Workflow_Status' || f.name === 'Workflow_Approver' || f.name === 'Workflow_Due_Date' || f.value === 'Workflow_Initiator' || f.value === 'CreatedBy') {
            return false
          } else return true
        })
        return fieldsLocal.length > 0
      },
      currentPermissions() {
        return {
          userAccess: this.accessUsers.map(user => user.value),
          groupAccess: this.accessGroups.map(group => group.value)
        }
      },
      desiredPendingRoute() { return this.$store.state.forms.desiredPendingRoute },
      ellipsisButtonConfig() { return this.ellipsisOpenType === 'form-layout' ? this.ellipsisButtonFormLayoutConfig : (this.ellipsisOpenType === 'enable-package' ? this.ellipsisButtonEnablePackageConfig : (this.ellipsisOpenType === 'disable-package' ? this.ellipsisButtonDisablePackageConfig : this.ellipsisButtonFieldConfig)) },
      filteredLayoutArray() {
        return !this.formLayoutArray ? [] : this.formLayoutArray.filter((formLayout) => {
          return formLayout.Name.toLowerCase().includes(this.lowerLayoutFilterString) || formLayout.Description.toLowerCase().includes(this.lowerLayoutFilterString)
        })
      },
      forms() { return this.$store.state.attributeForm.forms },
      groups() { return this.$store.state.common.groups },
      leaveMessage() {
        return this.activeForm.ID
          ? `Are you sure you want to leave? Changes to "${this.activeForm.name}" have not been saved.`
          : `Are you sure you want to leave? Changes to "${this.newFormName}" have not been saved.`
      },
      lowerFieldFilterString() { return !this.fieldFilterText ? '' : this.fieldFilterText.toLowerCase() },
      lowerLayoutFilterString() { return !this.layoutFilterText ? '' : this.layoutFilterText.toLowerCase() },
      newFormId() { return this.$store.state.attributeForm.newFormId },
      updatePromptMessage() { return 'Do you want to update all forms ?' },
      users() { return this.$store.state.common.users },
    },
    watch: {
      activeForm() {
        if (this.activeForm.ID) {
          this.isLoading = false
        }
      },
      desiredPendingRoute() { this.toggleLeaveDialog() },
    },
    created: async function () {
      this.ellipsisButtonFormLayoutConfig.buttons = [
        {
          id: 'copy-link',
          name: 'Copy Link',
          icon: 'VasionLinkIcon',
        },
        {
          id: 'embed-form-layout',
          name: 'Copy Embedded Link',
          icon: 'VasionCodeIcon',
        },
        {
          id: 'HR',
          name: 'HR',
          isHorizontalRule: true,
        },
        {
          id: 'edit-form-layout-button',
          name: `Edit ${$layoutsLabel}`,
          icon: 'VasionEditIcon',
        },
        {
          id: 'delete-form-layout-button',
          name: `Delete ${$layoutsLabel}`,
          icon: 'VasionDeleteIcon',
        },
        {
          id: 'copy-form',
          name: `Copy ${$layoutsLabel}`,
          icon: 'VasionFileCopyIcon',
        },
      ]
      this.selectedTabName = this.$store.state.forms.defaultFormTab ? this.$store.state.forms.defaultFormTab : 'fields'
      this.$store.dispatch('forms/setDefaultFormTab', '')

      if (this.$route.params.formID) {
        await this.$store.dispatch('attributeForm/populateIndexFormAdminData')
        this.getDetails(this.$route.params.formID)
      } else {
        this.toggleCreateNewForm()
      }
      this.saving = false
      if (!this.creatingNewForm) {
        this.isLoading = true
      }
    },
    updated: function () {
      this.$store.dispatch('mainViews/changeMainNav', `configure-form-${this.$route.params.formID}`)
    },
    methods: {
      async addFieldToFormat() {
        this.template += ` <%${this.selectedField.title}%>`
      },
      addNewForm: async function () {
        if (this.newFormName.match(/[<>;:'"/\\|?*_=+{}[\],.`~!@#$%^&()]/)) {
          this.snackbarTitle = 'Special Character Detected'
          this.snackbarSubTitle = 'Name cannot include special character(s).'
          this.snackbarImage = false
          this.showSnackbar = true
          return
        }

        if (this.newFormName.trim() === '') {
          this.snackbarTitle = 'Only Spaces Detected'
          this.snackbarSubTitle = 'Name cannot only include spaces.'
          this.snackbarImage = false
          this.showSnackbar = true
          return
        }

        const payload = {
          Value: this.newFormName,
        }

        this.isLoading = true
        await this.$store.dispatch('attributeForm/createNewForm', payload)
        if (this.$store.state.attributeForm.newFormId === "0") {
          this.snackbarTitle = 'Error creating object'
          this.snackbarSubTitle = 'Could not create object.  Check the name to make sure it is unique.'
          this.snackbarImage = false
          this.showSnackbar = true
          this.isLoading = false
          return
        }
        this.$store.dispatch('attributeForm/getForms')
        this.isLoading = false
        this.markAsClean()
        this.$router.push({ name: 'EditForm', params: { formID: this.newFormId } })
      },
      calculatedClass(selectedTab) { return this.selectedTabName === selectedTab ? 'text-selected' : 'text' },
      async copyToClipboard(data, type) {
        let url
        let baseURL = window.location.href
        const lastIndex = baseURL.indexOf('/forms/editForm')
        baseURL = baseURL.substring(0, lastIndex)
        const formName = encodeURI(data.Name)
        if (type === 'link') {
          url = `${baseURL}/formLink/${formName}/${data.EFormID}`
        } else {
          url = `<div style="width:100%;"><iframe src="${baseURL}/formLink/${formName}/${data.EFormID}" width="100%" height="100%" style="position:absolute" frameBorder="0" class="giphy-embed" allowFullScreen></iframe></div>`
        }

        let textCopySuccess = false
        try {
          // navigator is the better method, but can only be used in a secure context or localhost
          // meaning it fails in "http".
          // so, plan B is the hack in the "else" statement to add the text to a hidden text area and hope that works
          if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(url)
            textCopySuccess = true
          } else {
            const textArea = document.createElement("textarea")
            textArea.value = url
            // make the textarea out of viewport
            textArea.style.position = "fixed"
            textArea.style.left = "-999999px"
            textArea.style.top = "-999999px"
            document.body.appendChild(textArea)
            textArea.focus()
            textArea.select()

            textCopySuccess = document.execCommand('copy')
            textArea.remove();
          }
        } catch {
          textCopySuccess = false
        }

        this.snackbarTitle = textCopySuccess ? 'Copied!' : 'Could NOT Copy!'
        this.snackbarSubTitle = textCopySuccess ? 'Link copied to clipboard' : ''
        this.snackbarImage = true
        this.showSnackbar = true
      },
      async deleteField() {
        const payload = {
          fieldName: this.deleteFieldName,
          formID: this.activeForm.ID,
        }

        this.showGeneralModal = false
        this.isLoading = true
        const canDeleteResult = await this.$store.dispatch('document/canRemoveAttributeField', payload)

        if (canDeleteResult === '') {
          const index = this.fields.findIndex((f) => f.name === payload.fieldName)
          const fieldType = this.fields[index].type
          this.fields.splice(index, 1)
          this.fieldFilterText = ''

          const saveSuccess = await this.saveFormFields()
          if (saveSuccess) {
            if (fieldType === 2) {
              const payload = {
                IndexFormID: this.activeForm.ID,
                FieldToFilter: this.deleteFieldName,
                FilterFields: [],
              }
              this.$store.dispatch('document/saveFilterFields', payload)
            }

            this.snackbarTitle = `Successfully Removed Field "${payload.fieldName}"`
            this.snackbarSubTitle = ''
            this.snackbarImage = true
            this.showSnackbar = true
            await this.$amplitude.trackEvent('Field Removed', {})
          }
        } else {
          this.snackbarTitle = 'Cannot Remove Field'
          this.snackbarSubTitle = canDeleteResult
          this.snackbarImage = false
          this.showSnackbar = true
        }

        this.isLoading = false
      },
      cancelLeave() {
        this.routeTo = null
        this.toggleLeaveDialog()
      },
      async changePackageStatus(pkg, status) {
        const payload = {
          PackageId: pkg.PackageID,
          IsDisabled: !status,
        }
        const response = await this.$store.dispatch('forms/setPackageStatus', payload)
        if (response && response?.ResultStatus === 1) {
          this.snackbarTitle = "Package status was changed successfully";
          this.snackbarSubTitle = `Package: "${pkg.Name}"`;
          this.snackbarImage = true;
          this.showSnackbar = true;
          await this.getObjectPackages()
        } else {
          this.snackbarTitle = "Package status cannot be changed.";
          this.snackbarSubTitle = `Package: "${pkg.Name}"`;
          this.snackbarImage = false;
          this.showSnackbar = true;
        }
        this.showDisablePackagerPreventionPrompt = false
      },
      clickedOutsideLeave() { this.routeTo = null },
      async deleteFormLayout() {
        if (this.deleteFormLayoutID <= 0) {
            return
          }

          this.isLoading = true
          const response = await this.$store.dispatch('common/deleteEForm', this.deleteFormLayoutID)

          this.showGeneralModal = false
          this.snackbarTitle = response === '' ? `${$layoutsLabel} Deleted Successfully` : `Error Deleting ${$layoutsLabel}`
          this.snackbarSubTitle = response === '' ? `The ${$layoutsLabel} has been removed` : response
          this.snackbarImage = response
          this.showSnackbar = true
          this.isLoading = false

          this.loadFormLayoutsFromActiveForm()
      },
      async copyForm(EFormID) { this.$router.push({ name: 'TheNewFormView', params: { formID: this.activeForm.ID, formLayoutID: EFormID, isCopy: true } }) },
      async deletePackage(pkg) {
        const response = await this.$store.dispatch('forms/deletePackage', pkg.PackageID)
        if (response && response?.data?.ResultStatus === 1) {
          this.snackbarTitle = "Package Delete Successful";
          this.snackbarSubTitle = `Deleted package: "${pkg.Name}"`;
          this.snackbarImage = true;
          this.showSnackbar = true;
          await this.getObjectPackages()
        } else {
          this.snackbarTitle = "The package could not be deleted.";
          this.snackbarSubTitle = `Package: "${pkg.Name}"`;
          this.snackbarImage = false;
          this.showSnackbar = true;
        }
      },
      doLeave() {
        this.$store.dispatch('forms/updateAreFormsDirty', false)
        this.toggleLeaveDialog()
        this.isLoading = true

        if (this.routeTo && this.routeTo.name !== '') {
          if (!this.routeTo.params) {
            this.$router.push({ name: this.routeTo.name })
          } else {
            this.$router.push({ name: this.routeTo.name, params: this.routeTo.params })
          }

          this.routeTo = null
        } else {
          if (this.desiredPendingRoute.location === 'all-forms') {
            this.$router.push({ name: 'AllForms' })
          } else {
            this.$router.push({ name: 'EditForm', params: { formID: this.desiredPendingRoute.value } })
          }
          this.$store.dispatch('mainViews/changeMainNav', this.desiredPendingRoute.location)
        }
      },
      dragRowChildPrevent(event) {
        event.preventDefault()
        event.stopPropagation()
      },
      dragRowDrop(event, dropIndex) {
        event.preventDefault();
        const startIndex = this.dragRowStartIndex
        if (startIndex == dropIndex || startIndex + 1 == dropIndex) {
          return
        }

        const movedUp = dropIndex < startIndex
        //if Row moved up, then others have to be moved down (which means increase their field order)
        const displacement = movedUp ? 1 : -1

        // Set the field order for the dragged item to its new order
        this.fields[startIndex].fieldOrder = this.fields[dropIndex].fieldOrder
                
        // Adjust the field order for the items between the start and drop location
        this.fields.filter((f, ix) => {
          return (movedUp && ix >= dropIndex && ix < startIndex) || (!movedUp && ix > startIndex && ix <= dropIndex)
        }).forEach((f) => {
          f.fieldOrder += displacement
        })
        
        // Now move the fields
        if (!movedUp) {
          //if we moved the field down, then once we remove it from the fieldslist it offsets our dropIndex
          dropIndex -= 1
        }
        var element = this.fields[startIndex];
        this.fields.splice(startIndex, 1);
        this.fields.splice(dropIndex, 0, element);
        
        this.saveFormFields()
      },
      dragRowEnd() {
        this.dragRowTargetIndex = -1
        this.dragRowStartIndex = -1
      },
      dragRowOver(event, dropIndex) {
        if (this.dragRowStartIndex == dropIndex || this.dragRowStartIndex + 1 == dropIndex) {
          return
        }
        event.preventDefault();
        this.dragRowTargetIndex = dropIndex
      },
      dragRowStart(event, index) {
        this.dragRowStartIndex = index;
        event.dataTransfer.effectAllowed = 'move';
      },
      editFormLayout(formLayoutID) { this.$router.push({ name: 'TheNewFormView', params: { formID: this.activeForm.ID, formLayoutID: formLayoutID } }) },
      async editFormName() {
        const payload = {
                attributeFormID: this.activeForm.ID,
                attributeFormName: this.formName,
            }

        await this.$store.dispatch('attributeForm/renameForm', payload)
        this.updateNameInFormList(payload)
      },
      editPackage(packageId) {
        this.$router.push({ name: 'EditFormPackage', params: { formID: this.activeForm.ID, packageId: packageId } })
      },
      ellipsisButtonClicked(payload) {
        this.ellipsisClose()

        if (!payload || !payload.buttonId || !payload.item) {
          return
        }
        this.deletingEntityId = payload.buttonId
        switch (payload.buttonId) {
          case 'copy-form':
            this.copyForm(payload.item.EFormID)
            break;
          case 'copy-link':
            this.copyToClipboard(payload.item, 'link')
            break;
          case 'edit-field-button':
            this.toggleFieldEdit(payload.item.name)
            break;
          case 'embed-form-layout':
            this.copyToClipboard(payload.item, 'embed')
            break;
          case 'delete-field-button':
            this.generalModalMessage = `Are you sure you want to delete ${payload.item.name}?`
            this.showGeneralModal = true
            this.deleteFieldName = payload.item.name
            break
          case 'edit-form-layout-button':
            this.editFormLayout(payload.item.EFormID)
            break
          case 'delete-form-layout-button':
            this.generalModalMessage = `Are you sure you want to delete ${payload.item.Name}?`
            this.showGeneralModal = true
            this.deleteFormLayoutID = payload.item.EFormID
            break
          case 'edit-package-button':
            this.editPackage(payload.item.PackageID)
            break
          case 'delete-package-button':
            if(payload.item.HasInstances){
              if(!payload.item.IsDisabled){
                this.showMustDisablePackageToDeletePrompt = true
              } else {
                this.currentSelectedPackage = payload.item
                this.showDeleteConfirmation = true
              }
            }else {
              this.deletePackage(payload.item)
            }
            break
          case 'disable-package-button':
            this.currentSelectedPackage = payload.item
            this.showDisablePackagerPreventionPrompt = true
            break
          case 'enable-package-button':
            this.changePackageStatus(payload.item, true)
            break
          default:
            break
        }
      },
      ellipsisClose() { this.showEllipsisMenu = false },
      ellipsisOpen(event, item, openType) {
        this.ellipsisOpenType = openType
        this.ellipsisClose()
        this.ellipsisRowItem = item
        let displayY = event.pageY
        let baseSize = 0

        if (this.ellipsisButtonConfig && this.ellipsisButtonConfig.buttons) {
          baseSize = this.ellipsisButtonConfig.buttons.length * 40
        }

        if (displayY + baseSize > document.documentElement.scrollHeight) {
          displayY -= baseSize
        }

        displayY -= 40
        
        this.ellipsisMenuTopStyle = `top:${displayY}px;`
        this.showEllipsisMenu = true
      },
      fieldTypeText(fieldType) {
        const foundFieldType = this.fieldTypeList.find((element) => {
          return element.id === fieldType
        })
        return foundFieldType?.value
      },
      filteredFields() {
        return !this.fields ? [] : this.fields.filter((field) => {
          return field.name.toLowerCase().includes(this.lowerFieldFilterString) || field.title.toLowerCase().includes(this.lowerFieldFilterString)
        })
      },
      async generalModalOKClick() {
        if (this.deletingEntityId === 'delete-field-button') {
          this.deleteField()
        } else if (this.deletingEntityId === 'delete-form-layout-button') {
          this.deleteFormLayout()
        }
      },
      getDetails: async function (formId) {
        await this.$store.dispatch('attributeForm/getFormDetails', formId)
        this.setDataFromActiveForm()
        this.markAsClean()
        this.creatingNewForm = false
        this.editingField = false
        this.formName = this.activeForm.name
      },
      getIdValue: function (prefix, id) { return `${prefix}-${id}` },
      async getObjectPackages() {
        const response = await this.$store.dispatch('forms/getPackagesByObjectId', {Value: this.activeForm.ID})
        let packages = response?.packages ? cloneDeep(response.packages) : []
        await this.$store.dispatch('common/getIndexFieldsForForm', this.activeForm.ID)
        this.objectFields = this.$store.state.common.indexFields

        packages.forEach( (pkg, index) => {
          const field = this.objectFields.filter((field) => {
            return field.fieldID === pkg.PackageField
          })          
          packages[index].PackageField = field.length > 0 ? field[0].name : ""
          packages[index].active = packages[index].IsDisabled ? 'No' : 'Yes'
        })

        this.packages = packages
      },
      goToAllForms() { this.$router.push({ name: 'AllForms' }) },
      async loadFormLayoutsFromActiveForm() {
        const formLayoutResponse = await this.$store.dispatch('forms/getEFormsByFormID', this.activeForm.ID)
        this.formLayoutArray = formLayoutResponse && formLayoutResponse.EFormCollection && formLayoutResponse.EFormCollection.Values ? formLayoutResponse.EFormCollection.Values.slice() : []
      },
      markAsClean() { this.$store.dispatch('forms/updateAreFormsDirty', false) },
      markAsDirty() { this.$store.dispatch('forms/updateAreFormsDirty', true) },
      async newLayoutClick() { 
        await this.$store.dispatch('forms/clearEform')
        this.$router.push({ name: 'TheNewFormView', params: { formID: this.activeForm.ID, formLayoutID: '0' } }) 
      },
      async newPackageClick() {
        this.$router.push({ name: 'NewFormPackage', params: { formID: this.activeForm.ID } })
      },
      onPermissionsChanged() {
        this.markAsDirty()
      },
      promptUpdateField(payload) {
        this.showUpdateFieldsPrompt = !this.showUpdateFieldsPrompt
        this.updateFieldPayload = payload
      },
      reorderField: function (direction, fieldName) {
        // Get the field element and index for the field being moved
        const originalIndex = this.fields.findIndex((f) => f.name === fieldName);
        const fieldBeingMoved = this.fields[originalIndex]

        // Get the field element and index for the field being displaced
        const newIndex = direction === 'up' ? (originalIndex - 1) : (originalIndex + 1);

        // Set the new fieldOrder value for the field being moved
        const movedFieldOrder = this.fields[originalIndex].fieldOrder
        this.fields[originalIndex].fieldOrder = direction === 'up' ? (movedFieldOrder - 1) : (movedFieldOrder + 1);

        // Set the new fieldOrder value for the field being displaced
        const displacedFieldOrder = this.fields[newIndex].fieldOrder
        this.fields[newIndex].fieldOrder = direction === 'up' ? (displacedFieldOrder + 1) : (displacedFieldOrder - 1);

        // Now move the fields
        const newArray = this.fields.filter((f) => { // Remove the field being moved from the fields array
            return f.name !== fieldName
        })
        newArray.splice(newIndex, 0, fieldBeingMoved) // Stuff it back in, at it's new home

        this.fields = newArray;
        this.saveFormFields()
      },
      resetLocalFieldInfo() {
        this.editingFieldName = ''
        this.newFieldConfig.allowMultiples = true
        this.newFieldConfig.decimalPlaces = 0
        this.newFieldConfig.fieldUniqueName = { name: '<New Field>' }
        this.newFieldConfig.fieldLabel = ''
        // eslint-disable-next-line
        this.newFieldConfig.fieldType = this.fieldTypeList[0]
        this.newFieldConfig.fieldValues = ''
        this.newFieldConfig.fieldLength = 100
        this.fieldId = 0
        this.fieldOrder = 0
        this.newFieldConfig.fieldHidden = false
        this.newFieldConfig.mandatory = false
        this.piiData = false
        this.newFieldConfig.readOnly = false
        this.newFieldConfig.showCommas = false
        this.newFieldConfig.systemDate = false
        this.newFieldConfig.totalPageNo = false
        this.newFieldConfig.filterValues = false
        this.newFieldConfig.filterFieldsList = []
      },
      async saveFormFields() {
        this.isLoading = true
        const payload = {
          ID: this.activeForm.ID,
          fields: this.fields,
        }
        const response = await this.$store.dispatch('attributeForm/saveAttributeFormFields', payload)
        const result = response?.errorMessage === ''

        if (!result) {
          this.snackbarTitle = 'Error'
          this.snackbarSubTitle = 'Unable to update form fields'
          this.snackbarImage = false
          this.showSnackbar = true
          await this.getDetails(this.$route.params.formID)
        }
        else {
          response.fields?.forEach(f => {
             const index = this.fields.findIndex(lf => lf.fieldID === 0 && lf.name === f.name)
             if (index >= 0) {
                this.fields[index].fieldID = f.fieldID
             }
          })
        }

        this.fields.forEach(field => {
          if (field.type === 2) {
            const payload = {
              IndexFormID: this.activeForm.ID,
              FieldToFilter: field.name,
              FilterFields: (!field.filterValues) ? [] : field.filterFieldsList.filter(f => { return f.selected }).map(a => {
                return {
                  "BaseFieldValue": a.baseFieldValue,
                  "FilterFieldName": a.filterFieldName,
                  "FilterFieldValue": a.filterFieldValue,
                }
              }),
            }
            this.$store.dispatch('document/saveFilterFields', payload)
          }
        })

        await this.$store.dispatch('forms/getAllFieldHeaders')

        this.isLoading = false
        return result
      },
      async setDataFromActiveForm() {
        this.template = this.activeForm.documentNameTemplate
        this.formName = this.activeForm.name
        this.renameFiles = this.activeForm.renameFiles

        this.accessUsers = []
        this.accessGroups = []

        this.users.forEach((user) => {
          const newUser = {
            name: user.name,
            value: user.value,
          }

          if (this.activeForm.accessUserIDs && this.activeForm.accessUserIDs.includes(user.value)) {
            this.accessUsers.push(newUser)
          }
        })

        this.groups.forEach((group) => {
          const newGroup = {
            name: group.name,
            value: group.value,
          }

          if (this.activeForm.accessGroupIDs && this.activeForm.accessGroupIDs.includes(group.value)) {
            this.accessGroups.push(newGroup)
          }
        })

        this.fields = this.activeForm.fields

        for (const field of this.fields) {
          field.filterValues = false
          field.filterFieldsList = []
          if (field.type === 2) {
            let filterPayload = {
              IndexFormID: this.activeForm.ID,
              BaseFieldName: field.name
            }

            let filterResult = await this.$store.dispatch('document/getFilterFieldsForEditing', filterPayload)
            if (filterResult.FilterFieldsList) {
              if (filterResult.FilterFieldsList.length > 0) {
                field.filterValues = true
              }

              filterResult.FilterFieldsList.forEach(ff => {
                field.filterFieldsList.push(
                  {
                    "filterFieldName": ff.FilterFieldName,
                    "baseFieldValue": ff.BaseFieldValue,
                    "filterFieldValue": ff.FilterFieldValue,
                    "selected": true,
                  }
                )
              })
            }
          }
        }

        this.loadFormLayoutsFromActiveForm()
        await this.getObjectPackages()
      },
      shouldReorderDownShow(sequence) { return this.activeForm && this.activeForm.fields && (sequence + 1) < this.activeForm.fields.length },
      shouldReorderUpShow(sequence) { return sequence > 0 },
      showFailureSaveMessage(messageDetail) {
        this.snackbarTitle = `Unable to update ${$formsLabel}`
        this.snackbarSubTitle = messageDetail
        this.snackbarImage = false
        this.showSnackbar = true
      },
      async submitForm() {
        if (!this.validateSaveData()) {
          return
        }

        this.isLoading = true
        if (this.formName !== this.activeForm.name) {
          await this.editFormName()
        }

        const currentPermissions = this.$refs.permissionsComponent.getCurrentPermissions()
     
        const form = {
          ID: this.activeForm.ID,
          accessGroupIDs: currentPermissions.assignedGroups,
          accessUserIDs: currentPermissions.assignedUsers,
          documentNameTemplate: this.template,
          renameFiles: this.renameFiles,
        }

        const success = await this.$store.dispatch('attributeForm/saveAttributeFormHeader', form)
        this.isLoading = false
        this.$store.dispatch('forms/updateAreFormsDirty', false)
        if (success.Value === 'True') {
          this.saving = true
          this.goToAllForms()
        } else {
          this.showFailureSaveMessage(success)
        }
      },
      toggleCreateNewForm: function () { this.creatingNewForm = !this.creatingNewForm },
      toggleCheckbox(id) {
        this[id] = !this[id]
        this.markAsDirty()
      },
      toggleFieldEdit: function (fieldName) { this.$refs.newFieldDialog.toggleFieldEdit(fieldName) },
      toggleLeaveDialog() { this.showLeaveDialog = !this.showLeaveDialog },
      async updateFieldsInForms() {
        this.isLoading = true
        const response = await this.$store.dispatch('forms/updateFieldInEForms', this.updateFieldPayload)
        if (response === 'False') {
          this.snackbarTitle = 'Error!'
          this.snackbarSubTitle = 'Could not update all fields'
          this.snackbarImage = false
          this.showSnackbar = true
        }
        this.showUpdateFieldsPrompt = !this.showUpdateFieldsPrompt
        this.isLoading = false
      },
      updateNameInFormList: function (payload) {
          const form = this.forms.find((f) => f.value === payload.attributeFormID)
          if (form) {
              form.name = payload.attributeFormName
              this.activeForm.name = payload.attributeFormName
          }
      },
      updateSelectedTab(newSelectedTab) { this.selectedTabName = newSelectedTab },
      updateTable(fields) {
        this.fields = fields

        this.loadFormLayoutsFromActiveForm()
        this.saveFormFields()
      },
      validateSaveData() {
        this.formName = this.formName.trim()
        if (!this.formName) {
          this.showFailureSaveMessage(`The ${$formsLabel} Name Cannot be Blank or Only Include Whitespace`)
          return false
        }

        if (this.renameFiles && !this.template) {
          this.showFailureSaveMessage('The File Name Format cannot be empty if Rename Files is set')
          return false
        }

        // validate field uniqueness
        let errorMessage = ''
        const uniqueFieldNames = []

        this.fields.map((field) => {
          if (uniqueFieldNames.includes(field.name)) {
            errorMessage += `${field.name} is not a unique field name. `
            return false
          }

          uniqueFieldNames.push(field.name)
          return true
        })

        if (errorMessage) {
          this.showFailureSaveMessage(errorMessage)
          return false
        }

        return true
      },
    },
}
</script>

<style lang="scss" scoped>
  @import '@/assets/css/variables.scss';
  @import '@/assets/css/light-theme-system-palette.scss';

  #active-form-name::v-deep {
    input {
      color: black;
    }
  }

  .button-div {
    position: relative;
    left: 50%;
    top: 55%;
  }

  .tab-div {
    margin-left: -10px;
  }

  .field-dialog {
    width: 47%;
  }

  .dialogFooter {
    width: 100%;
    text-align: right;
  }

  .lightestGreyBackground {
    background-color: $grey-50;
  }

  .dropTarget {
    border-top: 6px solid $secondary;
  }

  .spacer {
    width: 2%;
  }

  .row {
    width: 100%;
    margin: 0px;
    display: flex;
    align-items: center;

    .row-field {
      margin: 0 16px 5px 0;

      &.checkbox {
        padding-top: 18px;
        width: 390px;
      }
    }
  }

  .right-text-align {
    text-align: right;
  }

  .pointer:hover {
    cursor: pointer;
  }

  .fields-table-container, .forms-table-container, .packages-table-container {
    width: 100%;
    overflow: auto;
    text-align: left;

    table {
      height: unset;
    }
  }

  .fields-table-container {
    height: calc(100vh - 454px);
  }

  .forms-table-container, .packages-table-container {
    height: calc(100vh - 338px);
  }

  .createFormDiv {
    display: flex;
    justify-content: center;
    padding-top: 20px;
  }

  .leftSideNavDiv {
    width: 304px;
    height: 100%;
    background-color: $grey-50;
    overflow: auto;
  }

  #field-list {
      min-width: 400px;
      .form-field-title {
          width: 200px;
          margin-left: 15px;
      }

      .form-field-type {
          width: 100px;
      }
      thead, tbody tr {
        display:table;
        width:100%;
        table-layout:fixed;/* even columns width , fix width of table too*/
      }
  }

  #index-form-submit {
      margin-top: 0px;
  }

  #name-edit-form, #create-new-field {
      padding: 10px;
      border: thin solid gray;
  }

  #create-new-form {
    padding: 10px;
    width: 500px;
    border: thin solid gray;
    text-align: right;
  }

  #create-new-field {
      padding: 10px;
      border: none;
  }

  #index-new-form-cancel {
      margin-left: 5px;
  }

  .vasion-html-table-default
  {
    padding-top: 20px;
  }

  .controls-column {
    width: 125px;
    text-align: right;
  }

  .icon-table-column {
    width: 40px;
    text-align: right;
    fill: $grey-400;
  }

  .field-mandatory-column {
    width: 100px;
  }

  .field-type-column {
    width: 200px;
  }

  .field-header-table {
    width: 100%;
    margin-top: 20px;
  }

  .field-header-filter-column {
    text-align: right;
    width: 300px;
  }

  .field-header-add-field-column {
    text-align: right;
    width: 100px;
  }
  .field-header-add-package-column {
    text-align: right;
  }
  .vasion-html-table-default table th label {
    font-weight: bold;
  }
  .layout-name-table-column {
    width: 40%;
  }

  .no-form-layouts-div {
    padding-top: 40px;
    width: 100%;
    text-align: center;
  }

  .no-form-layouts-label-div {
    margin-top: 10px;
  }

  .md-layout-item {
    width: 315px !important;
  }

  .scrollTBody {
    display: table-caption;
    caption-side: bottom;
    overflow: auto;
    max-height: calc(100vh - 523px);
    min-height: 200px;
  }
  .scrollTBodyFields {
    display: table-caption;
    caption-side: bottom;
    overflow: auto;
    max-height: calc(100vh - 407px);
    min-height: 200px;
  }

  table {
    // table-layout: fixed;
    width: 100%;
  }
  .fields-column {
    width: 20%;
  }
  .move-icon {
    cursor: move;
    fill: $grey-600;
  }

  .move-icon-column {
    width: 24px;
  }

  td.fields-column:nth-child(3) { // "Unique Name" column
    padding-left: 1px;
  }
  td.fields-column:nth-child(4) { // "Mandatory" column
    padding-left: 5px;
  }
  td.fields-column:nth-child(5) { // "Field Type" column
    padding-left: 6px;
  }
  .mb-0 {
    margin-bottom: 0px;
  }
  .vasion-table-packages {
    thead {
      font-family: "Archivo";
      font-size: 14px;
      font-weight: bold;
      font-style: normal;
      font-stretch: normal;
      line-height: normal;
      letter-spacing: normal;
      color: #707273;
    }
    tbody {
      font-family: "Archivo";
      font-size: 14px;
      font-weight: normal;
      font-style: normal;
      font-stretch: normal;
      line-height: normal;
      letter-spacing: normal;
      color: #707273;
      white-space: nowrap;
    }
    thead>tr>th:nth-child(1) {
      width: 40px;
    }
    tbody>tr>td:nth-child(1) {
      width: 40px;
      cursor: pointer;
      fill: #95989a;
      text-align: right;
    }

    thead>tr>th:nth-child(2) {
      width: 30vw;
    }
    tbody>tr>td:nth-child(2) {
      width: 30vw;
    }

    thead>tr>th:nth-child(5) {
      width: 60px;
    }
    tbody>tr>td:nth-child(5) {
      width: 50px;
      text-align: right;
    }

    thead>tr>th:not(:nth-child(1)):not(:nth-child(5)) {
      padding-left: 10px;
      padding-right: 10px;
    }
    tbody>tr>td:not(:nth-child(1)):not(:nth-child(5)) {
      padding-left: 10px;
      padding-right: 10px;
      cursor: pointer;
    }
  }
  .fix-last-col-width {
    width: 60px !important;
  }
  #api-tab {
    display: flex;
    flex-direction: column;
    height: calc(100vh - 270px);
  }
</style>
